import { useCallback } from 'react';
import { fetchAppStatus } from '../lib/deploy-api';
import { useDeployTracker } from '../contexts/DeployTrackerContext';
import type { ChatMessage } from '../components/chat/types';

const POLL_INTERVAL_MS = 10_000;
const POLL_MAX_ATTEMPTS = 60;

interface UseAppDeployPollingOptions {
  token: string;
  append: (msg: ChatMessage) => void;
  /** When true, success messages are rephrased to reflect that the app isn't actually live. */
  simulated?: boolean;
}

/**
 * Poll the app status until it reaches a terminal state (running, build
 * failed, or deploy failed), then post a final chat message. Stops after
 * POLL_MAX_ATTEMPTS × POLL_INTERVAL_MS without a verdict.
 */
export function useAppDeployPolling(options: UseAppDeployPollingOptions): (slug: string) => Promise<void> {
  const tracker = useDeployTracker();
  return useCallback(async (slug: string) => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      await wait(POLL_INTERVAL_MS);
      const snapshot = await fetchAppStatus(slug, options.token);
      if (!snapshot) continue;

      if (announceIfTerminal(snapshot, options.append, options.simulated ?? false)) {
        reportToTracker(snapshot, slug, tracker);
        return;
      }
    }
  }, [options, tracker]);
}

/**
 * Forward terminal deploy state to the global deploy tracker so the
 * persistent widget + off-page toast can react. Safe no-op if the slug
 * isn't tracked (e.g. polling started independently of the form).
 *
 * Only marks SUCCESS from the poller — failure transitions are owned by
 * the server-side SSE `failed` event so the poller can't race ahead of
 * a pipeline that's still retrying or mid-recovery.
 */
function reportToTracker(
  snapshot: Record<string, unknown>,
  slug: string,
  tracker: ReturnType<typeof useDeployTracker>,
): void {
  if (snapshot.deploy_status === 'running') {
    tracker.markSucceeded(slug);
  }
}

/** Sleep helper that unwraps a setTimeout Promise. */
function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Inspect an app snapshot; if it represents a terminal state, post the
 * appropriate chat message and return true.
 */
function announceIfTerminal(
  snapshot: Record<string, unknown>,
  append: (msg: ChatMessage) => void,
  simulated: boolean,
): boolean {
  if (snapshot.deploy_status === 'running') {
    append({ role: 'assistant', content: buildRunningMessage(snapshot, simulated), type: 'success' });
    return true;
  }
  if (snapshot.build_status === 'build_failed') {
    const buildLog = typeof snapshot.build_log === 'string'
      ? snapshot.build_log.slice(-500)
      : 'No logs available';
    append({
      role: 'assistant',
      content: `The build failed. Logs:\n\n\`\`\`\n${buildLog}\n\`\`\``,
      type: 'error',
    });
    return true;
  }
  if (snapshot.deploy_status === 'deploy_failed') {
    append({ role: 'assistant', content: 'Deployment failed.', type: 'error' });
    return true;
  }
  return false;
}

/**
 * Build the success message shown when the deploy reaches `running`. In
 * simulation mode, make it explicit that nothing is actually live so the
 * user doesn't waste time clicking a URL that can't resolve.
 */
function buildRunningMessage(snapshot: Record<string, unknown>, simulated: boolean): string {
  const url = String(snapshot.app_url ?? '');
  if (simulated) {
    return [
      'Simulated deploy recorded.',
      '',
      `No real container was built or deployed — the URL \`${url}\` will not resolve.`,
      'Point this Hub at a real GCP project + Kubernetes cluster to produce a live app.',
    ].join('\n');
  }
  return `Your app is live!\n\nURL: [${url}](${url})\nStatus: Running (healthy)\nSSO: Enabled (Keycloak)`;
}
